/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useFloating, offset, autoUpdate, flip, size, useMergeRefs } from '@floating-ui/react'
import { getStringWidth } from '@visx/text'
import { useEffect, useRef, useState } from 'react'
import { Tooltip } from '../Tooltip'
import { Button } from 'app/components/ui/button'
import type { ActivityContact } from 'app/types/entities'
import { bg } from 'lib/background'
import { getName } from 'app/components/LazyContactCell'

type Props = {
  contacts?: ActivityContact[]
  onContactClick?: (contactId: string) => void
}

type NamedContact = Pick<ActivityContact, 'id' | 'name'>

// The activities list already returns each linked contact's name, so a lookup is
// only needed for a contact that arrives without one. That lookup goes through
// LazyContactCell, which queries the contacts list (the single-id resolver throws
// for many existing contacts) and caches per id.
export async function getNames(contacts: NamedContact[]): Promise<NamedContact[]> {
  return Promise.all(
    contacts.map(async (contact) => {
      if (contact.name) return contact
      if (!contact.id || contact.id === `undefined`) return { ...contact, name: `Unknown` }
      return { ...contact, name: (await getName(contact.id)) ?? `Unknown` }
    }),
  )
}

export default function LinkedContactsCell({ contacts, onContactClick }: Props) {
  const [allContacts, setAllContacts] = useState<NamedContact[]>([])
  const [shownContacts, setShownContacts] = useState<NamedContact[]>([])
  const [hiddenContacts, setHiddenContacts] = useState<NamedContact[]>([])
  const observerRef = useRef<ResizeObserver>(undefined)
  const ref = useRef<HTMLButtonElement>(null)
  const data = useFloating({
    placement: `bottom-start`,
    middleware: [
      offset(({ rects }) => {
        return -rects.reference.height
      }),
      flip({
        fallbackAxisSideDirection: `start`,
        padding: 5,
      }),
      size({
        apply({ availableHeight, elements }) {
          elements.floating.style.width = elements.reference.getBoundingClientRect().width + `px`
          elements.floating.style.minHeight = elements.reference.getBoundingClientRect().height + `px`
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  })
  const buttonRefs = useMergeRefs([data.refs.setReference, ref])

  const getEstimatedWidth = (contactName: string) => {
    return (
      (getStringWidth(contactName, {
        fontSize: `14px`,
        lineHeight: `19.6px`,
        fontWeight: 400,
        fontFamily: `Inter, sans-serif`,
      }) ?? 0) + 20
    )
  }

  useEffect(() => {
    bg(async () =>
      getNames(contacts ?? [])
        .then((res) => {
          setAllContacts(res)
        })
        .catch(() => setAllContacts([])),
    )

    observerRef.current?.disconnect()

    return () => {
      setAllContacts([])
      setShownContacts([])
      setHiddenContacts([])
    }
  }, [contacts])

  useEffect(() => {
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    updateVisibleContacts()
  }, [allContacts])

  const updateVisibleContacts = () => {
    // this is a hacky way to get the actual available width by including space for the hidden contacts pill
    const canAllContactsFit =
      allContacts.reduce((acc, contact) => {
        return acc + getEstimatedWidth(contact.name ?? ``)
      }, 0) <
      (ref.current?.offsetWidth ?? 0) - 20
    const spaceForHiddenContactsPill = canAllContactsFit ? 20 : 50
    let availableWidth = (ref.current?.offsetWidth ?? 0) - spaceForHiddenContactsPill
    let visible: NamedContact[] = []
    let usedWidth = 0
    allContacts.forEach((contact) => {
      let contactNameWidth = getEstimatedWidth(contact.name ?? ``)
      if (usedWidth + contactNameWidth <= availableWidth) {
        visible.push(contact)
        usedWidth += contactNameWidth
      }
    })

    setShownContacts(visible)
    setHiddenContacts(allContacts.filter((contact) => !visible.includes(contact)))
  }

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (let entry of entries) {
        updateVisibleContacts()
      }
    })
    if (ref.current) {
      observerRef.current?.disconnect()
      resizeObserver.observe(ref.current)
      observerRef.current = resizeObserver
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [ref.current, allContacts])

  return (
    <div ref={buttonRefs} className="flex h-full w-full items-center space-x-1 px-2">
      {shownContacts?.map((contact, i) =>
        onContactClick && contact.id && contact.id !== `undefined` ? (
          <Button
            key={i}
            asChild
            variant="ghost"
            size="xs"
            className="h-5 rounded border-[0.5px] border-default bg-surface-muted px-2 text-label-xs font-normal capitalize text-default hover:bg-surface-strong"
          >
            <a
              href={`/contacts?drawer=${contact.id}`}
              x-component-name="Contact"
              data-opens-drawer=""
              onClick={(event) => {
                if (event.ctrlKey || event.metaKey) return
                event.preventDefault()
                onContactClick(contact.id)
              }}
            >
              {contact.name?.toLocaleLowerCase()}
            </a>
          </Button>
        ) : (
          <span key={i} x-component-name="Contact" className="tag space-x-2 bg-surface-subtle/25 px-2 capitalize">
            <span>{contact.name?.toLocaleLowerCase()}</span>
          </span>
        ),
      )}
      <Tooltip
        message={
          hiddenContacts.length ? (
            <div>
              {hiddenContacts?.map((contact, i) => (
                <div className="py-1 capitalize" key={i}>
                  {contact.name?.toLocaleLowerCase()}
                </div>
              ))}
            </div>
          ) : null
        }
      >
        {hiddenContacts?.length ? (
          <div className="tag cursor-default px-1.5">{`+${hiddenContacts?.length}`}</div>
        ) : null}
      </Tooltip>
    </div>
  )
}
